/**
* PURPOSE: Modal para editar datos base de un usuario existente.
* RESPONSIBILITIES: Actualiza username, rol, datos personales, estado, compañía principal, is_one_company y hashtags sin tocar permisos específicos.
* COLLABORATORS: src/services/user.js, src/utils/userState.js, src/utils/userHashtag.js, src/sections/UserSection.jsx
* ROUTE: src/admin/CreateUser/Modal/ModalEditUser/ModalEditUser.jsx
*/
import React, { useMemo, useState } from "react";
import Modal from "@mui/joy/Modal";
import ModalClose from "@mui/joy/ModalClose";
import Typography from "@mui/joy/Typography";
import Sheet from "@mui/joy/Sheet";
import { toast } from "react-hot-toast";
import { updateUser } from "../../../../services/user.js";
import { normalizeUserHashtagInput, parseUserHashtags } from "../../../../utils/userHashtag.js";
import { USER_STATE_OPTIONS } from "../../../../utils/userState.js";

const ModalEditUser = ({ open, setOpen, user, onUpdated }) => {
  const initialForm = useMemo(
    () => ({
      username: user?.username || "",
      password: "",
      rol: String(user?.rol ?? ""),
      tag_rol: user?.tag_rol || "",
      firstname: user?.firstname || "",
      surname: user?.surname || "",
      phone: user?.phone || "",
      email: user?.email || "",
      state: String(user?.state ?? 1),
      is_one_company:
        user?.is_one_company === true || Number(user?.is_one_company) === 1 ? "true" : "false",
      idcompany: user?.idcompany || "",
      hashtag: user?.hashtag || "",
    }),
    [user]
  );

  const [formData, setFormData] = useState(initialForm);
  const [loading, setLoading] = useState(false);

  const hashtagPreview = parseUserHashtags(formData.hashtag);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const buildPayload = () => {
    const isOneCompany =
      formData.is_one_company === true ||
      formData.is_one_company === "true" ||
      Number(formData.is_one_company) === 1;

    const rawIdCompany = String(formData.idcompany ?? "").trim();
    const parsedIdCompany = rawIdCompany === "" ? null : Number.parseInt(rawIdCompany, 10);

    const payload = {
      username: formData.username,
      rol: Number.parseInt(formData.rol, 10),
      tag_rol: formData.tag_rol,
      firstname: formData.firstname,
      surname: formData.surname,
      phone: formData.phone,
      email: formData.email,
      state: Number.parseInt(formData.state, 10),
      is_one_company: isOneCompany,
      idcompany: Number.isInteger(parsedIdCompany) && parsedIdCompany > 0 ? parsedIdCompany : null,
      hashtag: normalizeUserHashtagInput(formData.hashtag),
    };

    if (String(formData.password || "").trim()) {
      payload.password = formData.password;
    }

    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = buildPayload();

      if (payload.is_one_company === true && !payload.idcompany) {
        toast.error("Si el usuario es de una sola compañía, debes indicar un idcompany válido.");
        setLoading(false);
        return;
      }

      await updateUser(user.iduser, payload);
      toast.success("Usuario actualizado correctamente.");
      if (typeof onUpdated === "function") await onUpdated();
      setOpen(false);
    } catch (error) {
      console.error("Error al actualizar usuario", error);
      toast.error(error?.response?.data?.msg || "Error al actualizar usuario");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      aria-labelledby="modal-title"
      aria-describedby="modal-desc"
      open={open}
      onClose={() => setOpen(false)}
      sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
    >
      <Sheet
        variant="outlined"
        sx={{
          maxWidth: 800,
          width: "100%",
          borderRadius: "md",
          p: 3,
          boxShadow: "lg",
          height: { xs: "85%", md: "auto" },
          overflowY: "auto",
        }}
      >
        <ModalClose variant="plain" sx={{ m: 1 }} />

        <Typography component="h2" id="modal-title" level="h4" textColor="inherit" sx={{ fontWeight: "lg", mb: 2, textAlign: "center" }}>
          Editar Usuario
        </Typography>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Username" name="username" value={formData.username} onChange={handleChange} required />
          <Field label="Password (opcional)" name="password" type="password" value={formData.password} onChange={handleChange} placeholder="Dejar vacío para conservar" />

          <div className="flex flex-col">
            <label htmlFor="rol" className="text-sm font-medium text-gray-700">Rol</label>
            <select id="rol" name="rol" value={formData.rol} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 px-4 py-2 shadow-sm transition duration-200 ease-in-out hover:shadow-md focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm" required>
              <option value="1">1. Admin</option>
              <option value="2">2. Nivel de Acceso por Evento</option>
              <option value="3">3. Nivel de Acceso por Evento y Deporte</option>
              <option value="4">4. Companies</option>
            </select>
          </div>

          <Field label="Tag Rol" name="tag_rol" value={formData.tag_rol} onChange={handleChange} required />
          <Field label="First Name" name="firstname" value={formData.firstname} onChange={handleChange} />
          <Field label="Surname" name="surname" value={formData.surname} onChange={handleChange} />
          <Field label="Phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} />
          <Field label="Email" name="email" type="email" value={formData.email} onChange={handleChange} />

          <div className="flex flex-col">
            <label htmlFor="state" className="text-sm font-medium text-gray-700">Estado</label>
            <select id="state" name="state" value={formData.state} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 px-4 py-2 shadow-sm transition duration-200 ease-in-out hover:shadow-md focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm">
              {USER_STATE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label htmlFor="is_one_company" className="text-sm font-medium text-gray-700">Is One Company</label>
            <select id="is_one_company" name="is_one_company" value={formData.is_one_company} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 px-4 py-2 shadow-sm transition duration-200 ease-in-out hover:shadow-md focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm">
              <option value="false">false</option>
              <option value="true">true</option>
            </select>
          </div>

          <Field label="Id Company" name="idcompany" type="number" value={formData.idcompany} onChange={handleChange} min="1" step="1" placeholder="Ej: 3" />
          <Field label="Hashtag" name="hashtag" value={formData.hashtag} onChange={handleChange} placeholder="#volleyball #basketball" />

          {hashtagPreview.length > 0 ? (
            <div className="col-span-full flex flex-wrap gap-2">
              {hashtagPreview.map((tag) => (
                <span key={tag} className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-700 ring-1 ring-indigo-100">
                  {tag}
                </span>
              ))}
            </div>
          ) : null}

          <div className="col-span-full">
            <button type="submit" className="w-full rounded-md bg-blue-600 px-4 py-2 text-white transition duration-200 ease-in-out hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-60" disabled={loading}>
              {loading ? "Guardando..." : "Guardar cambios"}
            </button>
          </div>
        </form>
      </Sheet>
    </Modal>
  );
};

const Field = ({ label, name, type = "text", value, onChange, ...props }) => (
  <div className="flex flex-col">
    <label htmlFor={name} className="text-sm font-medium text-gray-700">{label}</label>
    <input id={name} name={name} type={type} value={value} onChange={onChange} className="mt-1 block w-full rounded-md border-gray-300 px-4 py-2 shadow-sm transition duration-200 ease-in-out hover:shadow-md focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm" {...props} />
  </div>
);

export default ModalEditUser;
